import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  SafetyTest,
  SafetyTestCategory,
  SafetyTestStatus,
  TestQuestion,
  TestAttempt,
  TestResultSummary,
  StartAttemptResponse,
  SubmitAttemptResponse
} from '../models/safety-test.model';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SafetyTestService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // Signal-based state
  private categoriesSignal = signal<SafetyTestCategory[]>([]);
  private testsSignal = signal<SafetyTest[]>([]);
  private currentAttemptSignal = signal<TestAttempt | null>(null);
  private currentQuestionsSignal = signal<TestQuestion[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals
  readonly categories = computed(() => this.categoriesSignal());
  readonly tests = computed(() => this.testsSignal());
  readonly currentAttempt = computed(() => this.currentAttemptSignal());
  readonly currentQuestions = computed(() => this.currentQuestionsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Computed stats
  readonly completedTests = computed(() =>
    this.testsSignal().filter(t => t.status === 'passed').length
  );

  readonly totalTests = computed(() => this.testsSignal().length);

  readonly expiredTests = computed(() =>
    this.testsSignal().filter(t => t.status === 'expired').length
  );

  readonly expiringSoonTests = computed(() =>
    this.testsSignal().filter(t => this.isExpiringSoon(t))
  );

  readonly requiredTests = computed(() =>
    this.testsSignal().filter(t => t.requiredAnnually)
  );

  constructor() {
    // Load initial data
    this.loadCategories();
    this.loadTests();
  }

  // ============================================
  // CATEGORY METHODS
  // ============================================

  /**
   * Load categories from API
   */
  async loadCategories(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<SafetyTestCategory[]>>(`${this.apiUrl}/safety-test-categories`)
      );

      if (response.success && response.data) {
        const categories = response.data.map(c => this.mapApiToCategory(c));
        this.categoriesSignal.set(categories);
      }
    } catch (error: any) {
      console.error('Error loading categories:', error);
    }
  }

  getCategories(): SafetyTestCategory[] {
    return this.categoriesSignal();
  }

  getCategoryById(id: string): SafetyTestCategory | undefined {
    return this.categoriesSignal().find(c => c.id === id);
  }

  // ============================================
  // SAFETY TEST METHODS
  // ============================================

  /**
   * Load available tests from API
   */
  async loadTests(): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/safety-tests`)
      );

      if (response.success && response.data) {
        const tests = response.data.map(t => this.mapApiToTest(t));
        this.testsSignal.set(tests);
      } else {
        throw new Error(response.error || 'Failed to load tests');
      }
    } catch (error: any) {
      console.error('Error loading tests:', error);
      this.errorSignal.set(error.message || 'Failed to load tests');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  getTests(): SafetyTest[] {
    return this.testsSignal();
  }

  getTestById(id: string): SafetyTest | undefined {
    return this.testsSignal().find(t => t.id === id);
  }

  /**
   * Fetch single test with details from API
   */
  async fetchTestById(id: string): Promise<SafetyTest | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any>>(`${this.apiUrl}/safety-tests/${id}`)
      );

      if (response.success && response.data) {
        return this.mapApiToTest(response.data);
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching test:', error);
      return null;
    }
  }

  /**
   * Get tests grouped by category
   */
  getTestsByCategory(): Map<string, SafetyTest[]> {
    const grouped = new Map<string, SafetyTest[]>();

    for (const test of this.testsSignal()) {
      const categoryId = test.categoryId || 'uncategorized';
      const existing = grouped.get(categoryId) || [];
      grouped.set(categoryId, [...existing, test]);
    }

    return grouped;
  }

  // ============================================
  // TEST ATTEMPT METHODS
  // ============================================

  /**
   * Start or resume a test attempt
   */
  async startTestAttempt(testId: string): Promise<StartAttemptResponse | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      // The API returns { success, data: attemptData, resumed: boolean }
      const response = await firstValueFrom(
        this.http.post<ApiResponse<any> & { resumed?: boolean }>(`${this.apiUrl}/test-attempts`, { testId })
      );

      if (response.success && response.data) {
        // Map the attempt data (returned directly in data, not data.attempt)
        const attempt = this.mapApiToAttempt(response.data);

        // Fetch questions separately using the question IDs from the attempt
        const questions = await this.fetchQuestionsForAttempt(testId, attempt.questionIds);

        this.currentAttemptSignal.set(attempt);
        this.currentQuestionsSignal.set(questions);

        // Update test status locally
        this.updateLocalTestStatus(testId, 'in_progress');

        return {
          attempt,
          questions,
          resumed: response.resumed || false
        };
      }
      throw new Error(response.error || 'Failed to start test');
    } catch (error: any) {
      console.error('Error starting test attempt:', error);
      this.errorSignal.set(error.message || 'Failed to start test');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Fetch questions for a test attempt
   */
  private async fetchQuestionsForAttempt(testId: string, questionIds: string[]): Promise<TestQuestion[]> {
    try {
      // Get test details which includes questions
      // API returns { success, data: { test, questions } }
      const response = await firstValueFrom(
        this.http.get<ApiResponse<{ test: any; questions: any[] }>>(`${this.apiUrl}/safety-tests/${testId}/questions`)
      );

      if (response.success && response.data) {
        // Questions are in response.data.questions, not response.data directly
        const rawQuestions = response.data.questions || [];
        let questions = rawQuestions.map((q: any) => this.mapApiToQuestion(q));

        // If questionIds provided, reorder questions to match
        if (questionIds && questionIds.length > 0) {
          const questionMap = new Map(questions.map((q: TestQuestion) => [q.id, q]));
          questions = questionIds
            .map(id => questionMap.get(id))
            .filter((q): q is TestQuestion => q !== undefined);
        }

        return questions;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching questions:', error);
      return [];
    }
  }

  /**
   * Get current attempt state
   */
  async getAttempt(attemptId: string): Promise<TestAttempt | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any>>(`${this.apiUrl}/test-attempts/${attemptId}`)
      );

      if (response.success && response.data) {
        const attempt = this.mapApiToAttempt(response.data.attempt);
        const questions = (response.data.questions || []).map((q: any) => this.mapApiToQuestion(q));

        this.currentAttemptSignal.set(attempt);
        this.currentQuestionsSignal.set(questions);

        return attempt;
      }
      return null;
    } catch (error: any) {
      console.error('Error getting attempt:', error);
      return null;
    }
  }

  /**
   * Save an answer (can be changed until submit)
   */
  async saveAnswer(attemptId: string, questionId: string, optionId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<void>>(`${this.apiUrl}/test-attempts/${attemptId}/answer`, {
          questionId,
          optionId
        })
      );

      if (response.success) {
        // Update local state
        this.currentAttemptSignal.update(attempt => {
          if (!attempt) return attempt;

          const existingAnswers = attempt.answers || [];
          const answerIndex = existingAnswers.findIndex(a => a.questionId === questionId);

          if (answerIndex >= 0) {
            existingAnswers[answerIndex] = { questionId, selectedOptionId: optionId };
          } else {
            existingAnswers.push({ questionId, selectedOptionId: optionId });
          }

          return {
            ...attempt,
            answers: existingAnswers,
            currentQuestionIndex: Math.max(
              attempt.currentQuestionIndex,
              attempt.questionIds.indexOf(questionId) + 1
            )
          };
        });
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Error saving answer:', error);
      this.errorSignal.set(error.error?.error || 'Failed to save answer');
      return false;
    }
  }

  /**
   * Submit test for grading
   */
  async submitTest(attemptId: string): Promise<TestResultSummary | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<SubmitAttemptResponse>>(`${this.apiUrl}/test-attempts/${attemptId}/submit`, {})
      );

      if (response.success && response.data) {
        const result = response.data;

        // Clear current attempt
        const testId = this.currentAttemptSignal()?.testId;
        this.currentAttemptSignal.set(null);
        this.currentQuestionsSignal.set([]);

        // Update test status locally
        if (testId) {
          this.updateLocalTestStatus(testId, result.passed ? 'passed' : 'failed', result.score);
        }

        // Reload tests to get fresh status
        this.loadTests();

        return {
          totalQuestions: result.totalQuestions,
          correctAnswers: result.correctAnswers,
          incorrectAnswers: result.incorrectAnswers,
          score: result.score,
          passed: result.passed,
          passingScore: result.passingScore,
          timeSpent: result.timeSpent,
          expiresAt: result.expiresAt
        };
      }
      throw new Error(response.error || 'Failed to submit test');
    } catch (error: any) {
      console.error('Error submitting test:', error);
      this.errorSignal.set(error.message || 'Failed to submit test');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get detailed results for a completed attempt
   */
  async getResults(attemptId: string): Promise<any | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any>>(`${this.apiUrl}/test-attempts/${attemptId}/results`)
      );

      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error: any) {
      console.error('Error getting results:', error);
      return null;
    }
  }

  /**
   * Get user's attempt history
   */
  async getMyAttempts(): Promise<TestAttempt[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/test-attempts/my-attempts`)
      );

      if (response.success && response.data) {
        return response.data.map(a => this.mapApiToAttempt(a));
      }
      return [];
    } catch (error: any) {
      console.error('Error getting attempts:', error);
      return [];
    }
  }

  // ============================================
  // STUDY GUIDE METHODS
  // ============================================

  /**
   * Get study guide download URL
   */
  async getStudyGuideUrl(testId: string): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<{ downloadUrl: string }>>(`${this.apiUrl}/safety-tests/${testId}/study-guide`)
      );

      if (response.success && response.data) {
        return response.data.downloadUrl;
      }
      return null;
    } catch (error: any) {
      console.error('Error getting study guide URL:', error);
      return null;
    }
  }

  /**
   * Open study guide in new tab
   */
  async viewStudyGuide(testId: string): Promise<void> {
    const url = await this.getStudyGuideUrl(testId);
    if (url) {
      window.open(url, '_blank');
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private updateLocalTestStatus(testId: string, status: SafetyTestStatus, score?: number): void {
    this.testsSignal.update(tests => {
      return tests.map(t => {
        if (t.id !== testId) return t;
        return {
          ...t,
          status,
          lastScore: score !== undefined ? score : t.lastScore,
          lastAttemptDate: new Date().toISOString().split('T')[0]
        };
      });
    });
  }

  getStatusText(status: SafetyTestStatus): string {
    switch (status) {
      case 'passed': return 'Passed';
      case 'failed': return 'Failed';
      case 'expired': return 'Expired';
      case 'in_progress': return 'In Progress';
      case 'new_version_available': return 'Update Available';
      case 'not_started':
      default: return 'Not Started';
    }
  }

  getStatusBadgeClass(status: SafetyTestStatus): string {
    switch (status) {
      case 'passed': return 'bg-success';
      case 'failed': return 'bg-danger';
      case 'expired': return 'bg-warning text-dark';
      case 'in_progress': return 'bg-info';
      case 'new_version_available': return 'bg-primary';
      case 'not_started':
      default: return 'bg-secondary';
    }
  }

  isExpiringSoon(test: SafetyTest): boolean {
    if (!test.expirationDate || test.status !== 'passed') return false;
    const expDate = new Date(test.expirationDate);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration > 0 && daysUntilExpiration <= 30;
  }

  getDaysUntilExpiration(test: SafetyTest): number {
    if (!test.expirationDate) return 0;
    const expDate = new Date(test.expirationDate);
    const now = new Date();
    return Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  getTimeRemaining(attempt: TestAttempt): string {
    if (!attempt.timeLimitExpiresAt) return '';

    const expiresAt = new Date(attempt.timeLimitExpiresAt);
    const now = new Date();
    const remainingMs = expiresAt.getTime() - now.getTime();

    if (remainingMs <= 0) return 'Time expired';

    const minutes = Math.floor(remainingMs / 60000);
    const seconds = Math.floor((remainingMs % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  getFrequencyText(category: SafetyTestCategory): string {
    switch (category.frequency) {
      case 'annual': return 'Annual';
      case 'biannual': return 'Every 2 years';
      case 'once': return 'One-time';
      case 'custom':
        if (category.frequencyDays) {
          return `Every ${category.frequencyDays} days`;
        }
        return 'Custom';
      default: return category.frequency;
    }
  }

  // ============================================
  // MAPPING METHODS
  // ============================================

  private mapApiToCategory(data: any): SafetyTestCategory {
    return {
      id: data.id,
      name: data.name,
      shortName: data.shortName || data.short_name,
      description: data.description,
      icon: data.icon || 'bi-clipboard-check',
      isRequired: data.isRequired ?? data.is_required ?? true,
      frequency: data.frequency || 'annual',
      frequencyDays: data.frequencyDays ?? data.frequency_days,
      sortOrder: data.sortOrder ?? data.sort_order ?? 0,
      organizationId: data.organizationId ?? data.organization_id
    };
  }

  private mapApiToTest(data: any): SafetyTest {
    return {
      id: data.id,
      categoryId: data.categoryId ?? data.category_id,
      category: data.category ? this.mapApiToCategory(data.category) : undefined,
      title: data.title,
      shortName: data.shortName ?? data.short_name ?? data.title,
      description: data.description,
      icon: data.category?.icon || data.icon || 'bi-clipboard-check',
      status: data.userStatus ?? data.status ?? 'not_started',
      lastAttemptDate: data.lastAttemptDate ?? data.last_attempt_date,
      lastScore: data.lastScore ?? data.last_score,
      passingScore: data.passingScore ?? data.passing_score ?? 70,
      expirationDate: data.expirationDate ?? data.expiration_date ?? data.expiresAt ?? data.expires_at,
      requiredAnnually: data.category?.isRequired ?? data.isRequired ?? data.is_required ?? true,
      questionCount: data.questionCount ?? data.question_count,
      timeLimit: data.timeLimit ?? data.time_limit,
      studyGuideUrl: data.studyGuideUrl ?? data.study_guide_url,
      version: data.version,
      shuffleQuestions: data.shuffleQuestions ?? data.shuffle_questions,
      shuffleOptions: data.shuffleOptions ?? data.shuffle_options
    };
  }

  private mapApiToAttempt(data: any): TestAttempt {
    return {
      id: data.id,
      testId: data.testId ?? data.test_id,
      test: data.test ? this.mapApiToTest(data.test) : undefined,
      profileId: data.profileId ?? data.profile_id,
      organizationId: data.organizationId ?? data.organization_id,
      startedAt: data.startedAt ?? data.started_at,
      completedAt: data.completedAt ?? data.completed_at,
      status: data.status || 'in_progress',
      score: data.score,
      passed: data.passed,
      correctAnswers: data.correctAnswers ?? data.correct_answers,
      totalQuestions: data.totalQuestions ?? data.total_questions ?? 0,
      currentQuestionIndex: data.currentQuestionIndex ?? data.current_question_index ?? 0,
      questionIds: data.questionIds ?? data.question_ids ?? [],
      timeLimitExpiresAt: data.timeLimitExpiresAt ?? data.time_limit_expires_at,
      timeSpentSeconds: data.timeSpentSeconds ?? data.time_spent_seconds,
      expiresAt: data.expiresAt ?? data.expires_at,
      answers: data.answers?.map((a: any) => ({
        questionId: a.questionId ?? a.question_id,
        selectedOptionId: a.selectedOptionId ?? a.selected_option_id,
        isCorrect: a.isCorrect ?? a.is_correct,
        answeredAt: a.answeredAt ?? a.answered_at
      })) || []
    };
  }

  private mapApiToQuestion(data: any): TestQuestion {
    return {
      id: data.id,
      text: data.questionText ?? data.question_text ?? data.text,
      type: data.questionType ?? data.question_type ?? data.type ?? 'multiple_choice',
      explanation: data.explanation,
      options: (data.options || []).map((o: any) => ({
        id: o.id,
        text: o.optionText ?? o.option_text ?? o.text,
        isCorrect: o.isCorrect ?? o.is_correct
      })),
      correctOptionId: data.correctOptionId ?? data.correct_option_id
    };
  }
}
